<script setup>
// SSR-safe Teleport: при включённом SSR Teleport ломает гидрацию поддерева,
// поэтому модалку рендерим только на клиенте (после mount).
import { ref as __ref, onMounted as __onMounted } from 'vue';
const __teleportReady = __ref(false);
__onMounted(() => { __teleportReady.value = true; });

import MainHeader from "@/Layouts/MainHeader.vue";
import AboutUsluga from "@/Layouts/AboutUsluga.vue";
import Prices from "@/Components/Prices.vue";
import VideoBlock from "@/Components/VideoBlock.vue";
import VKwidjet from "@/Components/VKwidjet.vue";
import OKwidjet from "@/Components/OKwidjet.vue";
import BreadcrumbsUslugi from "@/Components/BreadcrumbsUslugi.vue";
import Address from "@/Layouts/Address.vue";
import RevealPhone from "@/Components/RevealPhone.vue";
import MainFooter from "@/Layouts/MainFooter.vue";
import Tracking from '@/Components/ResourseLoader.vue';
import CourtPracticeList from "@/Layouts/CourtPracticeList.vue";
import UslugaDocuments from "@/Layouts/UslugaDocuments.vue";
import ReviewCarousel from "@/Layouts/ReviewCarousel.vue";
import UslugiQuiz from "@/Components/UslugiQuiz.vue";
import FlashMessage from "@/Components/FlashMessage.vue";
import MaxButton from "@/Components/MaxButton.vue";
import TelegramButton from "@/Components/TelegramButton.vue";
import BookingWidget from "@/Components/BookingWidget.vue";
import { Head } from "@inertiajs/vue3";
import { useModal } from "vue-final-modal";
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';

const vars = defineProps({
  // Закрывать ли страницу от индексации (приходит с сервера для пустых офферов)
  noindex: { type: Boolean, default: false },
  usluga: Object,
  userprices: Object,
  lawyers: "Object",
  lawyer: Object,
  practice: Object,
  courtCases: { type: Array, default: () => [] },
  // Практика темы из общей базы решений (блок «Практика»)
  themePractice: { type: Array, default: () => [] },
  flash: "Object",
  reviews: "Object",
  reviewscount: Number,
  rating: Number,
  work_topics: { type: Array, default: () => [] },
  main_usluga: Object,
  second_usluga: Object,
  city: Object,
  auth: Object,
  url: String,
  canonical: String,
  errors: Object,
  cityheader: Object,
  backendurl: String,
  statistics: Object,
  main_uslugi: { type: Array, default: () => [] },
  all_second_uslugi: { type: Array, default: () => [] },
  quiz_lawyers: { type: Array, default: () => [] },
  quiz_service_questions: { type: Object, default: () => ({}) },
  lawyer_articles_count: { type: Number, default: 0 },
  relatedUslugi: { type: Array, default: () => [] },
  lawyer_photos: { type: Array, default: () => [] },
  // Онлайн-запись на консультацию (только тестовые офферы, config services.booking)
  booking: { type: Boolean, default: false },
});

// Адрес офиса для записи на консультацию — первая строка адреса оффера
const bookingAddress = computed(() => String(vars.usluga?.address || '').split(/\r?\n/)[0].trim());

// SEO-перелинковка: только услуги с валидными связями (иначе URL не построить)
const relatedLinks = computed(() =>
  (vars.relatedUslugi || []).filter(r => r.cities?.url && r.main?.url && r.second?.url && r.url)
);

// Quiz для сайдбара и мобильной панели
const { open: openQuiz, close: closeQuiz } = useModal({
  component: UslugiQuiz,
  attrs: {
    get uslName()            { return vars.usluga.usl_name; },
    get lawyerName()         { return vars.lawyer?.name ?? ''; },
    get uslugaId()           { return vars.usluga.id; },
    get cityUrl()            { return vars.usluga.cities?.url ?? ''; },
    get allUslugi()          { return vars.main_uslugi; },
    get allSecondUslugi()    { return vars.all_second_uslugi; },
    get mainUslugaId()       { return vars.usluga.main_usluga_id; },
    get pageLawyer()         { return vars.lawyer; },
    get quizLawyers()        { return vars.quiz_lawyers; },
    get allServiceQuestions(){ return vars.quiz_service_questions; },
    onClose() { closeQuiz(); },
  },
});

// Показываем номер из поля «Телефон» услуги; если пуст — номер юриста
const displayPhone = computed(() =>
  vars.usluga.phone || (vars.lawyer?.phone ?? null)
);

const validQuestions = computed(() =>
  (vars.usluga.popular_question ?? []).filter(q => q.question?.trim())
);

const documents = computed(() => vars.usluga.borrowed_documents ?? []);

/**
 * Статьи-практика юриста плоским списком, свежие первыми.
 * С сервера они приходят сгруппированными по годам ({2025: [...], 2024: [...]}),
 * а когда статей нет — нулём.
 */
const lawyerArticles = computed(() => {
  const byYear = vars.practice;
  if (!byYear || typeof byYear !== 'object') return [];
  return Object.keys(byYear).sort().reverse()
    .flatMap((year) => (byYear[year] || []).map((a) => ({ ...a, year })));
});

const practiceCount = computed(() => lawyerArticles.value.length + (vars.themePractice?.length || 0));

const showMobileBar = ref(false);
const showStickyNav = ref(false);
const activeSection = ref('contacts');
const mobileNav = ref(null);

watch(activeSection, (section) => {
  const nav = mobileNav.value;
  if (!nav) return;
  const active = nav.querySelector(`[data-id="${section}"]`);
  if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
});

function checkScroll() {
  showMobileBar.value = window.scrollY > window.innerHeight * 0.6;
}

// Пока бар виден, он занимает нижний край экрана. Отдаём его высоту глобальной
// CSS-переменной, чтобы прочие прилипшие ко дну блоки (баннер cookie-согласия)
// поднялись над ним, а не перекрывали кнопки бара и не съедали клики.
const MOBILE_BAR_H = '68px';
watch(showMobileBar, (visible) => {
  document.documentElement.style.setProperty('--mobile-bar-h', visible ? MOBILE_BAR_H : '0px');
});

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

let observer = null;
let aboutObserver = null;
let ctaObserver = null;
onMounted(() => {
  // Липкая панель: видна, когда блок основных кнопок [Позвонить|MAX|TG] (#main-cta)
  // ушёл за верх экрана (или его нет в зоне видимости). Наблюдаем за ним, а не за
  // прокруткой — так панель не дублирует видимые кнопки и не опаздывает.
  const ctaEl = document.getElementById('main-cta');
  if (ctaEl) {
    ctaObserver = new IntersectionObserver(
      ([entry]) => { showMobileBar.value = !entry.isIntersecting; },
      { threshold: 0 }
    );
    ctaObserver.observe(ctaEl);
  } else {
    window.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
  }

  const sections = ['contacts', 'reviews', 'prices', 'vkclips', 'description', 'documents', 'court-cases', 'prctglr', 'questions'];
  observer = new IntersectionObserver(
    entries => entries.forEach(e => { if (e.isIntersecting) activeSection.value = e.target.id; }),
    { rootMargin: '-20% 0px -70% 0px' }
  );
  sections.forEach(id => {
    const el = document.getElementById(id);
    if (el) observer.observe(el);
  });

  const aboutEl = document.getElementById('about');
  if (aboutEl) {
    aboutObserver = new IntersectionObserver(
      ([entry]) => { showStickyNav.value = !entry.isIntersecting; },
      { threshold: 0 }
    );
    aboutObserver.observe(aboutEl);
  }
});
onUnmounted(() => {
  window.removeEventListener('scroll', checkScroll);
  observer?.disconnect();
  aboutObserver?.disconnect();
  ctaObserver?.disconnect();
  document.documentElement.style.setProperty('--mobile-bar-h', '0px');
});

const secondbannerimgmobile = "https://nedicom.ru/" + vars.usluga.mob_file_path;
const metaimage = "https://nedicom.ru/" + vars.usluga.file_path;

const canEdit = computed(() =>
  vars.auth && (vars.auth.isadmin == 1 || vars.auth.id == vars.usluga.user_id)
);

const descExpanded = ref(false);
const isLongDesc = computed(() =>
  (vars.usluga.longdescription || '').replace(/<[^>]+>/g, '').length > 300
);

// Принимает ссылку vk.com/clip-OID_VID, возвращает embed src
function toVkEmbed(url) {
  if (!url) return '';
  const m = url.match(/clip-(\d+)_(\d+)/);
  if (m) return `https://vk.com/clip_ext.php?oid=-${m[1]}&id=${m[2]}`;
  return url;
}
</script>

<template>
  <FlashMessage :message="vars.flash?.message" />

  <Head>
    <title>{{ vars.usluga.usl_name }} - {{ vars.city?.title }}</title>
    <meta head-key="description" name="description" :content="vars.usluga.usl_desc" />
    <meta property="og:title" :content="vars.usluga.usl_name" />
    <meta property="og:description" :content="vars.usluga.usl_desc" />
    <meta property="og:type" content="website" />
    <meta property="og:url" :content="'https://nedicom.ru/uslugi/' + vars.url" />
    <meta property="og:image" :content="metaimage" />
    <meta property="og:site_name" content="nedicom.ru" />
    <meta property="og:locale" content="ru_RU" />
    <link rel="canonical" :href="'https://nedicom.ru/uslugi/' + (vars.canonical || vars.url)" />
    <!-- В индекс не пускаем два случая:
         1) техническую страницу «все регионы» (all-cities, city.id=0) — она дубль реальной городской;
         2) оффер без содержимого (проп noindex приходит с сервера) — на остальных уровнях
            такое правило было, у оффера его не было вовсе. -->
    <meta v-if="vars.noindex || vars.city?.id === 0 || vars.city?.url === 'all-cities'" name="robots" content="noindex, follow" />
  </Head>

  <MainHeader :auth="vars.auth" :city="vars.cityheader" :hideBtn="true" />

  <!-- Мобильная горизонтальная навигация -->
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="opacity-0 -translate-y-1"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-1"
  >
  <nav v-show="showStickyNav" ref="mobileNav" class="lg:hidden sticky top-12 z-20 bg-white shadow-sm">
    <div class="flex overflow-x-auto border-b border-gray-200" style="scrollbar-width:none;-ms-overflow-style:none">
      <a href="#contacts" data-id="contacts"
        class="flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px"
        :class="activeSection==='contacts' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >Контакты</a>
      <a v-if="vars.reviewscount" href="#reviews" data-id="reviews"
        class="flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px"
        :class="activeSection==='reviews' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >Отзывы <span class="opacity-60">{{ vars.reviewscount }}</span></a>
      <a v-if="vars.userprices?.[0]" href="#prices" data-id="prices"
        class="flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px"
        :class="activeSection==='prices' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >Цены</a>
      <a v-if="vars.usluga.vk_clips?.length" href="#vkclips" data-id="vkclips"
        class="flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px"
        :class="activeSection==='vkclips' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >Видео</a>
      <a href="#description" data-id="description"
        class="flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px"
        :class="activeSection==='description' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >Описание</a>
      <a v-if="documents.length" href="#documents" data-id="documents"
        class="flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px"
        :class="activeSection==='documents' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >Документы</a>
      <a v-if="vars.courtCases && vars.courtCases.length" href="#court-cases" data-id="court-cases"
        class="flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px"
        :class="activeSection==='court-cases' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >Решения судов</a>
      <!-- Якорь на практику темы: число — сколько дел в блоке, а не статей юриста -->
      <a v-if="practiceCount" href="#prctglr" data-id="prctglr"
        class="flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px"
        :class="activeSection==='prctglr' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >Практика<span class="ml-1 opacity-60">{{ practiceCount }}</span></a>
      <a v-if="validQuestions.length" href="#questions" data-id="questions"
        class="flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px"
        :class="activeSection==='questions' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >Вопросы</a>
    </div>
  </nav>
  </Transition>

  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 py-6">
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">

        <!-- Sidebar -->
        <aside class="lg:col-span-1 order-2 lg:order-1">
          <div class="sticky top-16 space-y-2 max-h-[calc(100vh-5rem)] overflow-y-auto pr-0.5">

            <!-- Карточка юриста + навигация (только десктоп) -->
            <nav class="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                type="button"
                @click="scrollToTop"
                class="w-full text-left px-3 py-2.5 border-b border-gray-100 flex items-center gap-3 hover:bg-indigo-50/60 transition-colors"
                title="Наверх"
              >
                <img
                  v-if="vars.lawyer?.avatar_path"
                  :src="'https://nedicom.ru/' + vars.lawyer.avatar_path"
                  :alt="vars.lawyer.name"
                  loading="lazy"
                  class="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-indigo-100"
                />
                <div class="min-w-0">
                  <span class="block text-sm font-semibold text-gray-900 truncate">{{ vars.lawyer?.name }}</span>
                  <span v-if="vars.city" class="block text-xs text-gray-500 truncate">{{ vars.city.title }}</span>
                </div>
              </button>
              <div class="p-2 space-y-0.5">

                <a href="#contacts"
                  class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors"
                  :class="activeSection === 'contacts' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-700'"
                >
                  <span>Контакты</span>
                  <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </a>

                <a v-if="vars.reviewscount" href="#reviews"
                  class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors"
                  :class="activeSection === 'reviews' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-700'"
                >
                  <span>Отзывы</span>
                  <span class="text-xs bg-indigo-100 text-indigo-600 rounded-full px-2 py-0.5 font-medium">{{ vars.reviewscount }}</span>
                </a>

                <a v-if="vars.userprices?.[0]" href="#prices"
                  class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors"
                  :class="activeSection === 'prices' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-700'"
                >
                  <span>Цены</span>
                  <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </a>

                <a v-if="vars.usluga.vk_clips?.length" href="#vkclips"
                  class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors"
                  :class="activeSection === 'vkclips' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-700'"
                >
                  <span>Видео</span>
                  <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </a>

                <a href="#description"
                  class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors"
                  :class="activeSection === 'description' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-700'"
                >
                  <span>Описание</span>
                  <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </a>

                <a v-if="documents.length" href="#documents"
                  class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors"
                  :class="activeSection === 'documents' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-700'"
                >
                  <span>Документы</span>
                  <span class="text-xs bg-indigo-100 text-indigo-600 rounded-full px-2 py-0.5 font-medium">{{ documents.length }}</span>
                </a>

                <a v-if="vars.courtCases && vars.courtCases.length" href="#court-cases"
                  class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors"
                  :class="activeSection === 'court-cases' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-700'"
                >Решения судов</a>
                <a v-if="practiceCount" href="#prctglr"
                  class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors"
                  :class="activeSection === 'prctglr' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-700'"
                >
                  <span>Практика</span>
                  <span class="text-xs bg-indigo-100 text-indigo-600 rounded-full px-2 py-0.5 font-medium">{{ practiceCount }}</span>
                </a>

                <a v-if="validQuestions.length" href="#questions"
                  class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors"
                  :class="activeSection === 'questions' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-700'"
                >
                  <span>Вопросы</span>
                  <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </a>

              </div>

              <div v-if="canEdit" class="px-2 pb-2 border-t border-gray-100 pt-2">
                <a
                  :href="route('uslugi.edit', vars.usluga.id)"
                  class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-indigo-600 hover:bg-indigo-50 transition-colors font-medium"
                >
                  <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                  Редактировать
                </a>
              </div>
            </nav>

            <!-- Телефон юриста (десктоп, перенесён из шапки карточки) -->
            <div v-if="displayPhone" class="hidden lg:block">
              <RevealPhone
                :phone="displayPhone"
                :phoneto="'tel:' + displayPhone"
                :backendurl="vars.backendurl"
                :use-tracking-phone="false"
                :tracking="$page.props.tracking"
                :lawyer="vars.lawyer"
                :service-name="vars.usluga.usl_name"
                :rating="vars.rating"
                :reviewscount="vars.reviewscount"
              />
            </div>

          </div>
        </aside>

        <!-- Основной контент -->
        <main
          class="lg:col-span-3 order-1 lg:order-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
          itemscope
          itemtype="https://schema.org/Product"
        >
          <!-- Базовые свойства Product для микроразметки -->
          <meta itemprop="name" :content="vars.usluga.usl_name" />
          <meta itemprop="description" :content="vars.usluga.usl_desc || (vars.usluga.longdescription || '').replace(/<[^>]+>/g, ' ').trim().slice(0, 300) || vars.usluga.usl_name" />
          <meta itemprop="image" :content="metaimage" />

          <AboutUsluga
            :usluga="vars.usluga"
            :lawyer="vars.lawyer"
            :main_usluga="vars.main_usluga"
            :work-topics="vars.work_topics"
            :reviewscount="vars.reviewscount"
            :rating="vars.rating"
            :statistics="vars.statistics"
            :auth="vars.auth"
            :photos="vars.usluga.photos ?? []"
            :borrowed-photos="vars.usluga.borrowed_photos ?? []"
            :borrowed-usluga-photos="vars.usluga.borrowed_usluga_photos ?? []"
            :phone="displayPhone"
            :phoneto="'tel:' + displayPhone"
            :backendurl="vars.backendurl"
            :use-tracking-phone="false"
            :tracking="$page.props.tracking"
          />

          <!-- Редактировать (мобиль — под фото) -->
          <div v-if="canEdit" class="lg:hidden px-4 py-2 border-t border-gray-100">
            <a
              :href="route('uslugi.edit', vars.usluga.id)"
              class="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-indigo-600 bg-white border border-indigo-200 rounded-xl hover:bg-indigo-50 transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              Редактировать
            </a>
          </div>

          <!-- Контакты -->
          <div class="border-t border-gray-100">
            <Address
              v-if="vars.usluga.cities"
              :key="vars.backendurl"
              :tracking="$page.props.tracking"
              :backendurl="vars.backendurl"
              :usl_name="vars.usluga.usl_name"
              :usl_desc="vars.usluga.usl_desc"
              :region="vars.usluga.cities"
              :phone="displayPhone"
              :use-tracking-phone="false"
              :address="vars.usluga.address"
              :usluga_id="vars.usluga.id"
              :dopadress="vars.usluga.dopadress"
              :maps="vars.usluga.maps"
              :max="vars.usluga.max"
              :telegram="vars.usluga.telegram"
              :metaimage="metaimage"
              :company="vars.lawyer.name"
              :main_uslugi="vars.main_uslugi"
              :all_second_uslugi="vars.all_second_uslugi"
              :service_main_id="vars.usluga.main_usluga_id"
              :page_lawyer="vars.lawyer"
              :quiz_lawyers="vars.quiz_lawyers"
              :quiz_service_questions="vars.quiz_service_questions"
              desktop-phone-secondary
              :only-phone="vars.booking"
            />
          </div>

          <!-- Онлайн-запись на консультацию: пока только тестовые офферы (config services.booking) -->
          <div v-if="vars.booking" class="border-t border-gray-100">
            <BookingWidget
              :usluga-id="Number(vars.usluga.id)"
              :lawyer-name="vars.lawyer?.name || ''"
              :address="bookingAddress"
            />
          </div>

          <!-- Отзывы -->
          <div class="border-t border-gray-100 px-4 md:px-8 pt-2 pb-6 md:py-6">
            <ReviewCarousel
              :reviews="vars.reviews"
              :rating="vars.rating"
              :reviewscount="vars.reviewscount"
              :lwrid="vars.lawyer.id"
              :auth="vars.auth"
              :errors="vars.errors"
              :mainuslugaid="vars.usluga.main_usluga_id"
              :uslugaid="vars.usluga.id"
            />
          </div>

          <!-- Цены -->
          <div id="prices" class="border-t border-gray-100">
            <Prices
              :userprices="vars.userprices"
              :subheader="vars.main_usluga.usl_name"
              :city="vars.city"
              :reviewcoutnt="vars.main_usluga.mainreview_count"
              :rating="Number(vars.main_usluga.avg_review)"
              :secondbannerimgmobile="secondbannerimgmobile"
              :metaimage="metaimage"
              :keyword="vars.main_usluga.usl_name"
            />
          </div>

          <!-- ВК Клипы -->
          <div v-if="vars.usluga.vk_clips?.length" id="vkclips" class="border-t border-gray-100 py-8 px-4 md:px-8">
            <h2 class="mb-6 text-2xl font-bold tracking-tight text-gray-900">Видеоматериалы</h2>

            <!-- Мобиль: горизонтальный скролл -->
            <div class="flex lg:hidden overflow-x-auto gap-3 -mx-4 px-4 pb-2" style="scrollbar-width:none;-ms-overflow-style:none">
              <div
                v-for="(clip, i) in vars.usluga.vk_clips"
                :key="i"
                class="flex-shrink-0 relative overflow-hidden rounded-xl bg-black"
                style="height:min(68vh,460px);width:calc(min(68vh,460px)*9/16)"
              >
                <iframe
                  :src="toVkEmbed(clip)"
                  class="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock"
                  frameborder="0"
                  allowfullscreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>

            <!-- Десктоп: сетка -->
            <div class="hidden lg:grid grid-cols-3 gap-4">
              <div
                v-for="(clip, i) in vars.usluga.vk_clips"
                :key="i"
                class="relative w-full overflow-hidden rounded-xl bg-black"
                style="padding-top:177.78%"
              >
                <iframe
                  :src="toVkEmbed(clip)"
                  class="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock"
                  frameborder="0"
                  allowfullscreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>

          <!-- Описание -->
          <div id="description" class="border-t border-gray-100 py-8 px-4 md:px-8">
            <h2 class="mb-6 text-2xl font-bold tracking-tight text-gray-900">Подробнее</h2>
            <div class="relative">
              <div
                itemprop="disambiguatingDescription"
                class="richtext max-w-[68ch] space-y-4 text-gray-700 leading-relaxed transition-all duration-300 overflow-hidden"
                :style="isLongDesc && !descExpanded ? 'max-height: 16.25rem' : ''"
                v-html="vars.usluga.longdescription"
              ></div>
              <div
                v-if="isLongDesc && !descExpanded"
                class="absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-white to-transparent pointer-events-none"
              ></div>
            </div>
            <button
              v-if="isLongDesc"
              @click="descExpanded = !descExpanded"
              class="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:underline"
            >
              {{ descExpanded ? 'Свернуть' : 'Читать полностью' }}
              <svg class="w-4 h-4 transition-transform duration-300" :class="descExpanded ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
          </div>

          <!-- Документы -->
          <UslugaDocuments :documents="documents" />

          <!-- РЕШЕНИЯ СУДОВ.
               Отличается от блока «Практика» ниже принципиально: там статьи,
               которые юрист написал сам, здесь — акты, которые вынес суд.
               Номер дела, суд и дата проверяемы по картотеке, поэтому блок
               работает как доказательство, а не как рассказ о себе. В список
               попадают только дела, где lawyer_id указывает на этого юриста;
               прецеденты из открытых источников сюда не идут. -->
          <div v-if="vars.courtCases && vars.courtCases.length" id="court-cases" class="border-t border-gray-100 py-6 px-4 md:px-8 scroll-mt-24">
            <h2 class="mb-1 text-2xl font-bold tracking-tight text-gray-900">
              Решения судов по этой услуге
            </h2>
            <p class="text-sm text-gray-500 mb-4">
              Дела, которые вёл {{ vars.lawyer?.name || 'юрист' }}. Номер и суд можно проверить в картотеке.
            </p>

            <ol class="space-y-3">
              <li v-for="c in vars.courtCases" :key="c.id"
                  class="rounded-xl border border-gray-100 bg-white p-4">
                <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span class="inline-block rounded-lg bg-emerald-50 text-emerald-800 text-xs font-semibold px-2.5 py-1">
                    {{ c.outcome }}
                  </span>
                  <span class="text-sm font-semibold text-gray-900">Дело № {{ c.number }}</span>
                  <span class="text-xs text-gray-500">{{ c.date }}</span>
                </div>
                <div class="mt-1 text-xs text-gray-600">{{ c.court }}</div>
                <p v-if="c.summary" class="mt-2 text-sm text-gray-700 leading-snug">{{ c.summary }}</p>
                <a v-if="c.url" :href="c.url" target="_blank" rel="nofollow noopener"
                   class="mt-2 inline-flex items-center gap-1 text-xs text-indigo-700 hover:underline">
                  Карточка дела на сайте суда
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </li>
            </ol>
          </div>

          <!--
            Практика по теме. ⚠ КАРТИНОК БОЛЬШЕ НЕТ: здесь стояла галерея обложек
            статей, где три четверти площади занимало фото, а сути дела не было
            видно вовсе. Теперь — решения из нашей базы с судом, номером и датой.
          -->
          <div v-if="practiceCount" id="prctglr" class="border-t border-gray-100 py-6">
            <CourtPracticeList
              :articles="lawyerArticles"
              :cases="vars.themePractice"
              :keyword="vars.main_usluga.usl_name"
            />
          </div>

          <!-- Соцсети -->
          <div v-if="vars.usluga.vk || vars.usluga.ok" class="border-t border-gray-100 py-8 px-4 md:px-8">
            <h2 class="mx-auto max-w-5xl mb-6 text-2xl font-bold tracking-tight text-gray-900">
              Присоединяйтесь, чтобы не потерять контакты
            </h2>
            <VKwidjet v-if="vars.usluga.vk" :groupid="vars.usluga.vk" />
            <div class="flex justify-center">
              <OKwidjet v-if="vars.usluga.ok" :groupid="vars.usluga.ok" />
            </div>
          </div>

          <VideoBlock :video="vars.usluga.video" />

          <!-- Вопросы и ответы -->
          <div
            v-if="validQuestions.length"
            id="questions"
            class="border-t border-gray-100 py-8 px-4 md:px-8"
            itemscope
            itemtype="https://schema.org/FAQPage"
          >
            <h2 class="mb-6 text-2xl font-bold tracking-tight text-gray-900">Частые вопросы</h2>
            <ul class="divide-y divide-gray-100 list-none">
              <template
                v-for="item in validQuestions"
                :key="item.id ?? item.question"
              >
                <li
                  class="py-4"
                  itemscope
                  itemprop="mainEntity"
                  itemtype="https://schema.org/Question"
                >
                  <details class="group">
                    <summary class="flex items-center gap-3 cursor-pointer font-medium text-gray-900 hover:text-indigo-700 transition-colors list-none">
                      <svg class="w-4 h-4 text-indigo-400 transition-transform group-open:rotate-90 shrink-0"
                        xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                        <path fill-rule="evenodd"
                          d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
                      </svg>
                      <span itemprop="name">{{ item.question }}</span>
                    </summary>
                    <article
                      v-if="item.answer"
                      class="pt-3 pl-7 text-sm text-gray-500 leading-relaxed"
                      itemscope
                      itemprop="acceptedAnswer"
                      itemtype="https://schema.org/Answer"
                    >
                      <p itemprop="text">{{ item.answer }}</p>
                    </article>
                  </details>
                </li>
              </template>
            </ul>
          </div>

          <!-- Другие услуги в городе — внутренняя перелинковка (SEO + навигация) -->
          <div
            v-if="vars.city"
            class="border-t border-gray-100 py-8 px-4 md:px-8"
          >
            <h2 class="mb-6 text-2xl font-bold tracking-tight text-gray-900">
              Другие услуги юристов{{ vars.city?.title ? ' в городе ' + vars.city.title : '' }}
            </h2>

            <!-- По теме: родительская категория и подкатегория этого объявления -->
            <div
              v-if="(vars.main_usluga && vars.main_usluga.url && vars.main_usluga.url != 0 && vars.main_usluga.url !== '0') || (vars.second_usluga && vars.second_usluga.url && vars.second_usluga.url !== 'second')"
              class="flex flex-wrap gap-2 mb-6"
            >
              <a
                v-if="vars.main_usluga && vars.main_usluga.url && vars.main_usluga.url != 0 && vars.main_usluga.url !== '0'"
                :href="route('offer.main', [vars.city.url, vars.main_usluga.url])"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
              >
                {{ vars.main_usluga.usl_name }}
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
              </a>
              <a
                v-if="vars.second_usluga && vars.second_usluga.url && vars.second_usluga.url !== 'second'"
                :href="route('offer.second', [vars.city.url, vars.main_usluga.url, vars.second_usluga.url])"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
              >
                {{ vars.second_usluga.usl_name }}
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
              </a>
            </div>

            <div v-if="relatedLinks.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5">
              <a
                v-for="rel in relatedLinks"
                :key="rel.id"
                :href="route('uslugi.canonical.url', [rel.cities.url, rel.main.url, rel.second.url, rel.url])"
                class="group flex items-start gap-2 text-sm text-gray-700 hover:text-indigo-700 transition-colors"
              >
                <svg class="w-4 h-4 mt-0.5 text-indigo-300 group-hover:text-indigo-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
                <span>{{ rel.usl_name }}</span>
              </a>
            </div>
            <a
              v-if="vars.city?.url"
              :href="route('uslugi.url', [vars.city.url])"
              class="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700"
            >
              Все услуги юристов{{ vars.city?.title ? ' в городе ' + vars.city.title : '' }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
            </a>
          </div>

        </main>
      </div>
    </div>
  </div>

  <!-- Мобильная панель (появляется после скролла) -->
  <Teleport to="body" v-if="__teleportReady">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="showMobileBar"
        class="fixed bottom-0 inset-x-0 lg:hidden z-40 bg-white border-t border-gray-200 shadow-2xl"
      >
        <div class="flex items-center gap-3 px-4 py-3">
          <!-- MAX вместо заявки: написать в мессенджер быстрее, чем заполнять
               квиз. Если у юриста нет MAX — остаётся заявка. -->
          <!-- Страница с онлайн-записью: вместо MAX/заявки — «Записаться» к календарю -->
          <a
            v-if="vars.booking"
            href="#booking"
            class="flex-1 min-w-0 h-11 inline-flex items-center justify-center rounded-xl border border-indigo-200 bg-indigo-50 px-3 text-sm font-bold text-indigo-800 hover:border-indigo-400"
          >Записаться на консультацию</a>
          <MaxButton
            v-else-if="vars.usluga.max"
            :href="vars.usluga.max"
            flat
            label="Написать в MAX"
            goal="MAX_MOBILE_BAR"
            class="flex-1 min-w-0 h-11"
          />
          <button
            v-else
            type="button"
            @click="openQuiz"
            class="flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-4 text-gray-800 text-sm font-bold
                   bg-white border border-gray-300 rounded-xl shadow-sm transition-colors hover:bg-gray-50
                   focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
          >
            <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Оставить заявку
          </button>
          <TelegramButton
            v-if="vars.usluga.telegram && !vars.booking"
            :href="vars.usluga.telegram"
            icon-only
            goal="TG_MOBILE_BAR"
            class="flex-shrink-0 w-11 h-11"
          />
          <a
            v-if="displayPhone"
            :href="'tel:' + displayPhone"
            onclick="window.goal('PHONE_MOBILE_BAR');"
            class="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow transition-colors focus:outline-none focus:ring-2 focus:ring-brand-200 focus:ring-offset-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
          </a>
        </div>
      </div>
    </Transition>
  </Teleport>

  <BreadcrumbsUslugi
    v-if="vars.city"
    :city="vars.city ?? null"
    :main_usluga="vars.main_usluga?.url !== 0 ? vars.main_usluga : null"
    :second_usluga="vars.second_usluga ?? null"
    :usluga="vars.usluga"
  />

  <MainFooter />

  <Tracking :key="vars.backendurl" :tracking="$page.props.tracking" :backendurl="vars.backendurl" />
</template>
